/**
 * Sidebar variant for the "Constitution / Strategy" contour.
 *
 * Body: account dropdown, sub-nav (Constitution / Mission / Principles /
 * Strategy / Goals), live count badge for pending proposals, quick link
 * to "My drafts" (proposals the current user submitted that aren't
 * decided yet).
 *
 * Mirrors the visual contract of the University sidebar — same chrome,
 * account header, theme switch, sign-out, resize handle.
 */
import { useQuery } from '@tanstack/react-query';
import { Link, useMatchRoute } from '@tanstack/react-router';
import { ChevronsLeft, ChevronsUpDown, LogOut, Moon, Settings, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { authApi } from '../../lib/api';
import { constitutionApi, constitutionKeys } from '../../lib/api/constitution';
import { useResizablePanel } from '../../lib/use-resizable-panel';
import { cn } from '../../lib/utils';
import { useAuthStore } from '../../stores/auth-store';
import { useThemeStore } from '../../stores/theme-store';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

interface ConstitutionSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export function ConstitutionSidebar({ collapsed, onToggle }: ConstitutionSidebarProps) {
  const { t } = useTranslation();
  const matchRoute = useMatchRoute();
  const user = useAuthStore((s) => s.user);
  const workspaces = useAuthStore((s) => s.workspaces);
  const activeWorkspaceId = useAuthStore((s) => s.activeWorkspaceId);
  const setUnauthenticated = useAuthStore((s) => s.setUnauthenticated);
  const theme = useThemeStore((s) => s.theme);
  const setTheme = useThemeStore((s) => s.setTheme);
  const activeWorkspace = workspaces.find((w) => w.workspace.id === activeWorkspaceId)?.workspace;

  const { width, isDragging, onHandleMouseDown } = useResizablePanel({
    storageKey: 'chaos-sidebar-width',
    defaultWidth: 260,
    minWidth: 220,
    maxWidth: 380,
    side: 'left',
  });

  const proposalsQ = useQuery({
    queryKey: constitutionKeys.proposals({ status: 'pending' }),
    queryFn: () => constitutionApi.listProposals({ status: 'pending' }),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
  const pendingCount = proposalsQ.data?.proposals.length ?? 0;

  const myDrafts =
    proposalsQ.data?.proposals.filter(
      (p) =>
        p.proposerUserId === user?.id &&
        (p.status === 'pending_founder_approval' || p.status === 'in_review'),
    ) ?? [];

  const handleSignOut = async (): Promise<void> => {
    try {
      await authApi.logout();
    } catch {
      /* ignore */
    }
    setUnauthenticated();
    window.location.href = '/login';
  };

  if (collapsed) return null;

  const isConstitution = !!matchRoute({ to: '/constitution', fuzzy: true });
  const isMission = !!matchRoute({ to: '/mission' });
  const isPrinciples = !!matchRoute({ to: '/principles', fuzzy: true });
  const isStrategy = !!matchRoute({ to: '/strategy', fuzzy: true });
  const isGoals = !!matchRoute({ to: '/goals', fuzzy: true });
  const isProposals = !!matchRoute({ to: '/constitution/proposals' });

  return (
    <aside
      aria-label="Constitution navigation"
      style={{ width }}
      className={cn(
        'relative flex h-screen shrink-0 flex-col border-r border-border bg-muted/30',
        isDragging && 'select-none',
      )}
    >
      <div className="flex items-center gap-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="group flex flex-1 items-center justify-between px-3 py-2.5 text-left hover:bg-foreground/5"
              aria-label="Account menu"
            >
              <div className="flex min-w-0 flex-col leading-tight">
                <span className="truncate text-[13px] font-semibold tracking-tight text-foreground">
                  {activeWorkspace?.name ?? user?.displayName ?? user?.email ?? t('app.name')}
                </span>
                <span className="truncate text-[11px] text-muted-foreground">
                  {user?.email ?? ''}
                </span>
              </div>
              <ChevronsUpDown
                className="h-3.5 w-3.5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
                aria-hidden
              />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" side="bottom" className="w-56">
            <DropdownMenuLabel>
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium">{user?.displayName ?? user?.email}</span>
                <span className="text-xs text-muted-foreground">{user?.email}</span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              <span>{theme === 'dark' ? t('nav.light_mode') : t('nav.dark_mode')}</span>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/settings/profile">
                <Settings className="h-4 w-4" />
                <span>{t('settings.profile.title')}</span>
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleSignOut}>
              <LogOut className="h-4 w-4" />
              <span>{t('dashboard.sign_out')}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <button
          type="button"
          onClick={onToggle}
          aria-label="Collapse sidebar"
          className="mr-1.5 flex h-7 w-7 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground"
        >
          <ChevronsLeft className="h-4 w-4" aria-hidden />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 pb-2 pt-3">
        <div className="px-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/80">
          Governance
        </div>
        <ul className="mt-1 flex flex-col">
          <NavRow to="/constitution" active={isConstitution && !isProposals} label="Constitution" />
          <NavRow to="/mission" active={isMission} label="Mission" />
          <NavRow to="/principles" active={isPrinciples} label="Principles" />
          <NavRow to="/strategy" active={isStrategy} label="Strategy" />
          <NavRow to="/goals" active={isGoals} label="Goals" />
          <NavRow
            to="/constitution/proposals"
            active={isProposals}
            label="Proposals"
            badge={pendingCount}
          />
        </ul>

        {myDrafts.length > 0 ? (
          <>
            <div className="mt-4 px-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/80">
              My drafts
            </div>
            <ul className="mt-1 flex flex-col">
              {myDrafts.slice(0, 8).map((p) => (
                <li key={p.proposalId}>
                  <Link
                    to="/constitution/proposals"
                    className="flex items-center gap-1.5 rounded px-2 py-1 text-[12px] text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground"
                    title={p.title}
                  >
                    <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full bg-warning" />
                    <span className="truncate">{p.title}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </nav>

      <button
        type="button"
        aria-label="Resize sidebar"
        tabIndex={-1}
        onMouseDown={onHandleMouseDown}
        className={cn(
          'absolute right-0 top-0 z-10 h-full w-1 cursor-col-resize border-0 bg-transparent p-0',
          'transition-colors',
          isDragging ? 'bg-foreground/20' : 'hover:bg-foreground/10',
        )}
      />
    </aside>
  );
}

interface NavRowProps {
  to:
    | '/constitution'
    | '/constitution/proposals'
    | '/mission'
    | '/principles'
    | '/strategy'
    | '/goals';
  active: boolean;
  label: string;
  badge?: number;
}

function NavRow({ to, active, label, badge }: NavRowProps) {
  return (
    <li>
      <Link
        to={to}
        className={cn(
          'flex items-center justify-between rounded px-2 py-1 text-[13px] transition-colors',
          active
            ? 'bg-foreground/5 text-foreground'
            : 'text-muted-foreground hover:bg-foreground/5 hover:text-foreground',
        )}
      >
        <span>{label}</span>
        {badge != null && badge > 0 ? (
          <span className="ml-auto rounded-full border border-warning/40 bg-warning/10 px-1.5 py-px font-mono text-[10px] font-medium tabular-nums text-warning-foreground">
            {badge}
          </span>
        ) : null}
      </Link>
    </li>
  );
}
